// --- Logic --------------------------------------------------------------------------------------

/**
 * Shared styling for every MUI outlined field in the dashboard: pill outline, Alexandria type and a
 * floating label that notches the border. Direction has to be threaded through because MUI anchors
 * the label and the select arrow to the left edge by default, which is wrong in Arabic.
 */
export const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": { color: "var(--color-brand)" },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-full)",
        "& fieldset": { borderColor: "var(--color-gray-01)", borderWidth: "1.5px" },
        "& fieldset legend": {
            textAlign: dir === "rtl" ? "right" : "left",
            marginLeft: dir === "rtl" ? "auto" : undefined,
            marginRight: dir === "rtl" ? "0px" : undefined,
        },
        "&:hover fieldset": { borderColor: "var(--color-gray-02)" },
        "&.Mui-focused fieldset": { borderColor: "var(--color-brand)", borderWidth: "1.5px" },
        "&.Mui-disabled": { backgroundColor: "var(--color-light)" },
        "&.Mui-disabled fieldset": { borderColor: "var(--color-gray-01)" },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
        "&.Mui-disabled": { WebkitTextFillColor: "var(--color-gray-03)" },
    },
    "& .MuiSelect-select": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px !important",
        paddingRight: dir === "rtl" ? "14px !important" : "32px !important",
        paddingLeft: dir === "rtl" ? "32px !important" : "14px !important",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-icon": {
        right: dir === "rtl" ? "unset" : 7,
        left: dir === "rtl" ? 7 : "unset",
    },
});

/**
 * The same look shrunk to fit a table row. A cell is ~52px tall and sits beside six others, so the field
 * drops its floating label and most of its padding — everything else stays identical, because a table
 * styling its own inputs would drift from the forms the moment either one changed.
 */
export const getCellFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    /* Zero intrinsic width, so an open editor never widens the `max-content` grid track it sits in. */
    minWidth: 0,
    width: "100%",
    "& .MuiInputBase-root": { width: "100%", minWidth: 0 },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-lev4)",
        "& fieldset": { borderColor: "var(--color-brand)", borderWidth: "1.5px" },
        "&:hover fieldset": { borderColor: "var(--color-brand)" },
        "&.Mui-focused fieldset": { borderColor: "var(--color-brand)", borderWidth: "1.5px" },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "6px 10px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-select": {
        fontFamily: "Alexandria, sans-serif",
        minWidth: "0 !important",
        padding: "6px 10px !important",
        paddingRight: dir === "rtl" ? "10px !important" : "28px !important",
        paddingLeft: dir === "rtl" ? "28px !important" : "10px !important",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-icon": {
        right: dir === "rtl" ? "unset" : 4,
        left: dir === "rtl" ? 4 : "unset",
    },
});

/** Menus portal out of the table, so their type and direction have to be set where they actually render. */
export const getCellMenuProps = (dir: "rtl" | "ltr") => ({
    /** `slotProps.paper`, not `PaperProps` — the latter was dropped in MUI v9. */
    slotProps: {
        paper: {
            sx: {
                direction: dir,
                borderRadius: "var(--radius-lev4)",
                boxShadow: "var(--shadow-big-8)",
                "& .MuiMenuItem-root": {
                    fontFamily: "Alexandria, sans-serif",
                    fontSize: "13px",
                    color: "var(--color-dark)",
                    justifyContent: dir === "rtl" ? "flex-end" : "flex-start",
                },
                "& .MuiMenuItem-root.Mui-selected": { backgroundColor: "var(--color-shaded-brand)" },
            },
        },
    },
});

/**
 * The filter bar's own size: short enough to sit in a 52px bar, wide enough to read a status in. Shares
 * the pill outline and the floating label with the forms, so the bar does not read as a third design.
 */
export const getFilterFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    minWidth: 150,
    /* A filter never shrinks — the bar scrolls sideways instead, so labels stay readable at any width. */
    flex: "0 0 auto",
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "12px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": { transform: dir === "rtl" ? "translate(-12px, 9px) scale(1)" : "translate(12px, 9px) scale(1)" },
        "&.MuiInputLabel-shrink": { transform: dir === "rtl" ? "translate(-12px, -8px) scale(0.75)" : "translate(12px, -8px) scale(0.75)" },
        "&.Mui-focused": { color: "var(--color-brand)" },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "12.5px",
        height: "38px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-full)",
        "& fieldset": { borderColor: "var(--color-gray-01)", borderWidth: "1.5px" },
        "& fieldset legend": { textAlign: dir === "rtl" ? "right" : "left" },
        "&:hover fieldset": { borderColor: "var(--color-gray-02)" },
        "&.Mui-focused fieldset": { borderColor: "var(--color-brand)", borderWidth: "1.5px" },
    },
    "& .MuiSelect-select": {
        fontFamily: "Alexandria, sans-serif",
        padding: "8px 12px !important",
        paddingRight: dir === "rtl" ? "12px !important" : "30px !important",
        paddingLeft: dir === "rtl" ? "30px !important" : "12px !important",
        textAlign: dir === "rtl" ? "right" : "left",
    },
    "& .MuiSelect-icon": { right: dir === "rtl" ? "unset" : 6, left: dir === "rtl" ? 6 : "unset" },
});
